"use client";

import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";

const ACCENT = "#bff745";

function useInView<T extends Element>(threshold = 0.3) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function VisualFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative rounded-lg border border-border bg-bg p-6 sm:p-8 overflow-hidden"
      style={{
        boxShadow:
          "0 0 0 1px rgba(191,247,69,0.08), 0 24px 60px -20px rgba(191,247,69,0.18)",
      }}
    >
      <div aria-hidden className="absolute left-5 top-5 h-px w-10 bg-accent" />
      <div aria-hidden className="absolute left-5 top-5 w-px h-10 bg-accent" />
      <div aria-hidden className="absolute bottom-5 right-5 size-1.5 rounded-full bg-accent" />
      {children}
    </div>
  );
}

/* ───────── STEP 01 — Brand Brief, typewriter rows ───────── */

function TypeOnce({
  text,
  startMs,
  speedMs = 28,
  active,
}: {
  text: string;
  startMs: number;
  speedMs?: number;
  active: boolean;
}) {
  const [shown, setShown] = useState("");
  useEffect(() => {
    if (!active) {
      setShown("");
      return;
    }
    let i = 0;
    let interval: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        i++;
        setShown(text.slice(0, i));
        if (i >= text.length) clearInterval(interval);
      }, speedMs);
    }, startMs);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [active, text, startMs, speedMs]);
  const done = shown.length >= text.length;
  return (
    <span className="text-small text-text">
      {shown}
      {!done && active && (
        <span aria-hidden className="ml-px inline-block w-[1px] h-3 align-middle bg-accent animate-pulse" />
      )}
    </span>
  );
}

export function DiscoveryVisual() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);
  const rows: Array<[string, string]> = [
    ["Brand", "Streetwear, UK"],
    ["Audience", "18–28, urban"],
    ["Catalogue", "12 SKUs at launch"],
    ["Timeline", "10 weeks to live"],
    ["Budget", "Phased delivery"],
  ];
  return (
    <div ref={ref}>
      <VisualFrame>
        <p
          className="text-small font-semibold uppercase tracking-[0.18em] text-text-muted transition-all duration-500 ease-out"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(6px)",
          }}
        >
          Scoping document
        </p>
        <p
          className="text-h3 mt-2 text-text transition-all duration-500 ease-out"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(8px)",
            transitionDelay: "120ms",
          }}
        >
          Brand <span className="text-accent">brief.</span>
        </p>
        <dl className="mt-6 divide-y divide-border border-y border-border">
          {rows.map(([k, v], idx) => (
            <div
              key={k}
              className="grid grid-cols-[90px_1fr] sm:grid-cols-[120px_1fr] py-3"
            >
              <dt className="text-small text-text-muted">{k}</dt>
              <dd>
                <TypeOnce text={v} startMs={500 + idx * 200} active={inView} />
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-5 text-small text-text-muted">
          Two weeks · costed plan · signed off before any build starts
        </p>
      </VisualFrame>
    </div>
  );
}

/* ───────── STEP 02 — Build progress tracker ───────── */

export function BuildVisual() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);
  const items: Array<{ label: string; target: number }> = [
    { label: "Logo Design", target: 100 },
    { label: "Shopify Store", target: 100 },
    { label: "Product Pages", target: 100 },
    { label: "SEO Setup", target: 80 },
    { label: "Content Writing", target: 60 },
  ];
  return (
    <div ref={ref}>
      <VisualFrame>
        <div className="flex items-baseline justify-between">
          <p className="text-small font-semibold uppercase tracking-[0.18em] text-text-muted">
            Build progress
          </p>
          <span className="text-small text-text-muted tabular-nums">
            Week <CountNumberInline to={6} active={inView} delay={0} durationMs={900} /> / 10
          </span>
        </div>
        <p className="text-h3 mt-2 text-text">
          Five tracks <span className="text-accent">in flight.</span>
        </p>
        <ul className="mt-6 space-y-4">
          {items.map((item, idx) => {
            const complete = item.target >= 100;
            const delay = idx * 300;
            return (
              <li key={item.label}>
                <div className="flex items-center justify-between gap-3 text-small">
                  <span className="flex items-center gap-2 text-text">
                    <span
                      className={[
                        "inline-flex size-4 items-center justify-center rounded-full transition-all duration-300",
                        inView && complete
                          ? "bg-accent text-accent-ink scale-100 opacity-100"
                          : "bg-bg-soft text-text-muted/40 scale-90 opacity-70",
                      ].join(" ")}
                      style={{ transitionDelay: `${delay + 800}ms` }}
                      aria-hidden
                    >
                      {complete ? <CheckCircle2 className="size-3" strokeWidth={3} /> : null}
                    </span>
                    {item.label}
                  </span>
                  <CountNumber to={item.target} active={inView} delay={delay} suffix="%" small />
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-bg-soft overflow-hidden">
                  <div
                    className="h-full rounded-full transition-[width] duration-[1200ms] ease-out"
                    style={{
                      width: inView ? `${item.target}%` : "0%",
                      transitionDelay: `${delay}ms`,
                      backgroundColor: ACCENT,
                      boxShadow: `0 0 12px rgba(191,247,69,0.4)`,
                    }}
                    aria-hidden
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </VisualFrame>
    </div>
  );
}

/* ───────── STEP 03 — Launch stats dashboard ───────── */

function CountNumber({
  to,
  active,
  delay = 0,
  suffix = "",
  durationMs = 1200,
  small = false,
}: {
  to: number;
  active: boolean;
  delay?: number;
  suffix?: string;
  durationMs?: number;
  small?: boolean;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }
    let raf: number | undefined;
    const start = setTimeout(() => {
      const startTime = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - startTime) / durationMs);
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(eased * to));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(start);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, to, delay, durationMs]);
  return (
    <span
      className={[
        "tabular-nums font-semibold",
        small ? "text-small text-text-muted" : "text-text",
      ].join(" ")}
    >
      {value}
      {suffix}
    </span>
  );
}

export function LaunchVisual() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);
  const stats: Array<{ label: string; to: number; suffix?: string; accent?: boolean }> = [
    { label: "Store Live", to: 100, suffix: "%", accent: true },
    { label: "Orders Day 1", to: 24, accent: true },
    { label: "Google Indexed", to: 47, suffix: " pages" },
    { label: "Brand Score", to: 92, suffix: "/100", accent: true },
  ];
  return (
    <div ref={ref}>
      <VisualFrame>
        <div className="flex items-baseline justify-between">
          <p className="text-small font-semibold uppercase tracking-[0.18em] text-text-muted">
            Launch dashboard
          </p>
          <span
            className={[
              "inline-flex items-center gap-1.5 rounded-pill border border-accent/40 bg-accent/10 px-2.5 py-1 text-[0.625rem] font-bold uppercase tracking-wider text-accent",
              inView ? "opacity-100" : "opacity-0",
            ].join(" ")}
            style={{ transition: "opacity 400ms ease 800ms" }}
          >
            <span className="size-1.5 rounded-full bg-accent animate-pulse" /> Live
          </span>
        </div>
        <p className="text-h3 mt-2 text-text">
          Day one — <span className="text-accent">on the board.</span>
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          {stats.map((s, idx) => (
            <div
              key={s.label}
              className={[
                "rounded-md border bg-bg-soft p-4 transition-all duration-500",
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
                s.accent ? "border-accent/30" : "border-border",
              ].join(" ")}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <p className="text-[0.6875rem] uppercase tracking-wider text-text-muted">
                {s.label}
              </p>
              <p className="mt-2">
                <span
                  className={[
                    "tabular-nums font-bold",
                    s.accent ? "text-accent" : "text-text",
                  ].join(" ")}
                  style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", lineHeight: 1 }}
                >
                  <CountNumberInline
                    to={s.to}
                    suffix={s.suffix ?? ""}
                    active={inView}
                    delay={idx * 150 + 200}
                  />
                </span>
              </p>
            </div>
          ))}
        </div>
        <p className="mt-5 text-small text-text-muted">
          First customers, first impressions, first rankings — all live.
        </p>
      </VisualFrame>
    </div>
  );
}

function CountNumberInline({
  to,
  active,
  delay = 0,
  suffix = "",
  durationMs = 1400,
}: {
  to: number;
  active: boolean;
  delay?: number;
  suffix?: string;
  durationMs?: number;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }
    let raf: number | undefined;
    const start = setTimeout(() => {
      const startTime = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - startTime) / durationMs);
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(eased * to));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(start);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, to, delay, durationMs]);
  return (
    <>
      {value}
      {suffix}
    </>
  );
}
